import { useQuery } from '@tanstack/react-query'

import { TreeNode } from 'common/components/catalog/Catalog'
import { useAppSelector } from 'common/hooks/redux'

import { fetchCustomerAttributes } from '../api'
import { personalizationKeys as keys } from '../queryKeys'

export type CustomerAttributes = {
  fileName: string
  type: string
  created: string
  columns: string[]
  data: Object[]
}

function removeColumnsWithTooManyValues({
  data,
  maxValues,
}: {
  data: TreeNode
  maxValues: number
}): TreeNode {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (Object.keys(value).length <= maxValues) {
      acc[key] = value
    }
    return acc
  }, {})
}

const useGetCustomerAttributesQuery = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const getCustomerAttributesQuery = useQuery(
    keys.customerAttributes(accountId),
    () => fetchCustomerAttributes(accountId),
    {
      enabled: !!accountId,
      retry: false,
      select: (data) => {
        const mappedData: TreeNode = Object.fromEntries(
          data.map((item) => {
            if (item.type === 'list') {
              return [item.name, item.values]
            }
            return [item.name, item]
          })
        )

        return removeColumnsWithTooManyValues({
          data: mappedData,
          maxValues: 100,
        })
      },
      meta: {
        errorMessage: 'The customer attributes could not be loaded',
      },
    }
  )

  return getCustomerAttributesQuery
}

export default useGetCustomerAttributesQuery
